// @repo/notify — notification key derivation. The key is derived by the
// package — callers never compute it — from `kind::source::recipient`, with
// `::occurrence` appended only when the injected kind config declares one.

import { type KindConfig, resolveKindConfig } from "./registry";
import type { Report, ResourceRef } from "./types";

function refString(ref: ResourceRef): string {
  return `${ref.resourceType}/${ref.id}`;
}

/** A key taken apart again. Reference fields stay strings — the key stores
 *  `resourceType/id`, and splitting those is `notificationSource`'s job. */
export interface ParsedNotificationKey {
  kind: string;
  source: string;
  recipient: string;
  occurrence?: string;
}

/**
 * The inverse of `notificationKey`. Lives here because the format is this
 * file's invention: a reader that splits on `::` itself is a second definition
 * of the format, and it drifts the first time the key changes.
 *
 * Returns undefined for anything that isn't a well-formed key rather than a
 * partially-filled object — a caller can't act on half a key.
 */
export function parseNotificationKey(key: string): ParsedNotificationKey | undefined {
  const parts = key.split("::");
  if (parts.length < 3 || parts.length > 4) return undefined;
  const [kind, source, recipient, occurrence] = parts;
  if (!kind || !source || !recipient) return undefined;
  return { kind, source, recipient, occurrence };
}

export function notificationKey(report: Report, kinds: Record<string, KindConfig>): string {
  const config = resolveKindConfig(report.kind, kinds);

  if (config.occurrence && !report.occurrence) {
    throw new Error(`@repo/notify: "${report.kind}" reports require an occurrence`);
  }
  if (!config.occurrence && report.occurrence) {
    throw new Error(`@repo/notify: "${report.kind}" reports carry no occurrence`);
  }

  const parts = [report.kind, refString(report.source), refString(report.recipient)];
  if (config.occurrence) parts.push(report.occurrence as string);
  return parts.join("::");
}
